"use client";

/**
 * FormModal's part in a multi-step CTA: report completion to the runner (fresh submit, or Continue
 * from an earlier submission), show a "Step N saved" toast when the next step opens, and decide
 * when the "Step X of Y" label still applies. Everything is inert without a `sequence`, so a
 * standalone form behaves exactly as before.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import type { FormField } from "@/lib/database/forms";
import { endsFormEarly } from "@/lib/forms/conditional-visibility";
import type { SubmittedFormResponse } from "@/features/forms/_logic/viewer-form";
import { extractStepContact, type CtaStepSequenceContext } from "../_logic/cta-steps";
import type { FormSubmissionPhase } from "./useFormSubmissionState";

interface UseFormStepSequenceParams {
  sequence: CtaStepSequenceContext | undefined;
  submissionPhase: FormSubmissionPhase;
  submissionRecap: SubmittedFormResponse | null;
  formFields: FormField[];
  visibleFields: FormField[];
  formData: Record<string, unknown>;
  emailTemplates: unknown;
  respondentTypeId: number | null;
  showToast: (message: string, type: "success" | "error") => void;
}

interface UseFormStepSequenceReturn {
  /** Handler for the recap's "Continue to next step", or undefined when there is nothing to continue to. */
  onContinueFromRecap: (() => void) | undefined;
  /** Whether to show "Step X of Y": false outside a sequence and once this step ended it. */
  showStepLabel: boolean;
  /** Word for the form's own pagination: "Page" inside a sequence (which owns "Step"), else "Step". */
  paginationLabel: "Page" | "Step";
  /** Suffix for the dialog's accessible name, e.g. ", step 1 of 2", or "" outside a sequence. */
  dialogLabelSuffix: string;
  /** Pass to `useFormSubmitFlow`: an edit save completes the step. Undefined outside a sequence. */
  onEditSaved: ((recap: SubmittedFormResponse) => void) | undefined;
}

/**
 * Wires a FormModal into a multi-step CTA.
 * @param params - See {@link UseFormStepSequenceParams}
 * @returns See {@link UseFormStepSequenceReturn}
 * @throws Never
 */
export function useFormStepSequence({
  sequence,
  submissionPhase,
  submissionRecap,
  formFields,
  visibleFields,
  formData,
  emailTemplates,
  respondentTypeId,
  showToast,
}: UseFormStepSequenceParams): UseFormStepSequenceReturn {
  // States
  // Report once per mount. The modal remounts for every step, and the runner also ignores repeats,
  // but this keeps the toast from firing twice.
  const hasReportedRef = useRef(false);

  // Derived values
  const onStepComplete = sequence?.onStepComplete;
  const hasNextStep = !!sequence?.hasNextStep;
  // An earlier submission can still move the sequence on, unless its saved answers ended the form
  // early (e.g. an RSVP answered "not attending").
  const canContinueFromRecap = useMemo(
    () => hasNextStep && !!submissionRecap && !endsFormEarly(formFields, submissionRecap.answers, respondentTypeId),
    [hasNextStep, submissionRecap, formFields, respondentTypeId]
  );
  const endedEarlyOnSubmit = useMemo(
    () => submissionPhase === "submitted" && endsFormEarly(formFields, formData, respondentTypeId),
    [submissionPhase, formFields, formData, respondentTypeId]
  );
  // Once this step ended the flow (skip-to-end answer, or a recap with nowhere to continue),
  // "Step 1 of 2" would promise a next step that isn't coming.
  const hasEndedSequence =
    endedEarlyOnSubmit || (hasNextStep && submissionPhase === "previously-submitted" && !canContinueFromRecap);
  // Opened from the overview, the dialog's header already shows where the step sits.
  const showStepLabel = !!sequence && sequence.origin === "run" && !hasEndedSequence;
  const onEditingChange = sequence?.onEditingChange;
  const isEditing = submissionPhase === "idle" || submissionPhase === "editing";
  const dialogLabelSuffix = sequence ? `, step ${sequence.stepNumber} of ${sequence.stepCount}` : "";

  // Handlers
  const handleContinueFromRecap = useCallback(() => {
    if (!onStepComplete || !submissionRecap || hasReportedRef.current) return;
    hasReportedRef.current = true;
    const contact = extractStepContact(formFields, submissionRecap.answers, emailTemplates);
    onStepComplete({ contact: { ...contact, email: contact.email ?? submissionRecap.email }, endedEarly: false });
  }, [onStepComplete, submissionRecap, formFields, emailTemplates]);

  /** An edit to an earlier submission saved: that completes the step too (e.g. back to the overview). */
  const handleEditSaved = useCallback(
    (recap: SubmittedFormResponse) => {
      if (!onStepComplete || hasReportedRef.current) return;
      hasReportedRef.current = true;
      const contact = extractStepContact(formFields, recap.answers, emailTemplates);
      onStepComplete({
        contact: { ...contact, email: contact.email ?? recap.email },
        endedEarly: endsFormEarly(formFields, recap.answers, respondentTypeId),
      });
    },
    [onStepComplete, formFields, emailTemplates, respondentTypeId]
  );

  // Effects
  // Tell the dialog whether a form is open for input, so leaving can ask about unsaved changes.
  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [onEditingChange, isEditing]);

  // Report a fresh submission once. Every submit path (plain, paid, free tier) ends in the
  // "submitted" phase, so this is the single place completion is detected. Only visible answers
  // count, so a value left in a skipped field can't be carried forward.
  useEffect(() => {
    if (!onStepComplete || submissionPhase !== "submitted" || hasReportedRef.current) return;
    hasReportedRef.current = true;
    const visibleAnswers = Object.fromEntries(visibleFields.map((field) => [field.label, formData[field.label]]));
    onStepComplete({
      contact: extractStepContact(visibleFields, visibleAnswers, emailTemplates),
      endedEarly: endedEarlyOnSubmit,
    });
    // The next step replaces this modal (and its thank-you screen) straight away, so confirm the
    // save here. The toast host lives above the modals, so it outlives this one.
    if (hasNextStep && !endedEarlyOnSubmit && sequence) showToast(`Step ${sequence.stepNumber} saved`, "success");
  }, [
    onStepComplete,
    submissionPhase,
    visibleFields,
    formData,
    emailTemplates,
    endedEarlyOnSubmit,
    hasNextStep,
    sequence,
    showToast,
  ]);

  return {
    onContinueFromRecap: canContinueFromRecap ? handleContinueFromRecap : undefined,
    showStepLabel,
    paginationLabel: sequence ? "Page" : "Step",
    dialogLabelSuffix,
    onEditSaved: sequence ? handleEditSaved : undefined,
  };
}
